import React from "react";
import { Link } from "react-router-dom";

export interface FaqQuestion {
  answer: React.ReactNode;
  question: React.ReactNode;
  slug: string;
}

export interface FaqCategory {
  name: string;
  questions: FaqQuestion[];
  slug: string;
}

export const faqCategories: FaqCategory[] = [
  {
    slug: "divida-ativa",
    name: "Dívida Ativa",
    questions: [
      {
        slug: "divida-ativa-1",
        question: <><strong>O que é Dívida Ativa</strong> do município?</>,
        answer: (
          <>
            <p> É o conjunto dos créditos devidos à Fazenda Pública Municipal que, quando não pagos até a data de seu vencimento, são cobrados pela Procuradoria da Dívida Ativa, administrativa ou judicialmente. Esses créditos podem ser tributários, provenientes do não pagamento dos tributos, bem como não tributários, tendo sua liquidez e certeza apuradas pelo órgão que os originou. Os créditos são inscritos em um livro eletrônico e deles é extraída uma Certidão de Dívida Ativa, conhecida como CDA. </p> <p> Legislação: art. 1º, §1º, §2º e art. 6º, caput, do decreto nº 13.601/2015. </p>
          </>
        ),
      },
      {
        slug: "divida-ativa-2",
        question: <><strong>Como saber</strong> se tenho Dívida Ativa?</>,
        answer: (
          <>
            <p> Basta acessar o Portal PGM em <Link to="/consultar/debitos/em_aberto">www.portal.pgm.fortaleza.ce.gov.br</Link> e clicar em Serviços {">"} Consultar Dívidas. Digite seu CPF ou CNPJ. Caso tenha Dívida Ativa, aparecerá como resultado. </p>
          </>
        ),
      },
      {
        slug: "divida-ativa-3",
        question: <>Existe um <strong>valor mínimo para inscrição</strong> do débito na Dívida Ativa do município?</>,
        answer: (
          <>
            <p> Sim. Não haverá inscrição na Dívida Ativa de débito de um mesmo devedor com a Fazenda Municipal de valor consolidado igual ou inferior a R$ 50,00 (cinquenta reais). Em relação ao ajuizamento, somente serão cobrados judicialmente créditos cujo valor consolidado seja superior a R$ 50.000,00 (cinquenta mil reais), que deve ser apurado de maneira consolidada por espécie de tributo ou natureza do débito quando não tributária, considerando-se o mesmo sujeito passivo, somados todos os encargos e os acréscimos legais ou contratuais. Valores inferiores podem ser cobrados por via administrativa: entre elas, o protesto em Cartório. </p> <p> Legislação: Art. 10 e 17, da Lei Complementar nº 358/2023. <br /> Portaria nº 136/2023 - GPG/PGM. <br /> Art. 1º, § 1º, da Lei Complementar nº 239/2017. </p>
          </>
        ),
      },
      {
        slug: "divida-ativa-4",
        question: <>Como <strong>emitir o boleto</strong> da sua dívida?</>,
        answer: (
          <>
            <p> Na página inicial, acesse o Portal do Contribuinte, na opção <Link to="/consultar/debitos/em_aberto">Consultar Dívidas</Link>, realize a busca pelo CPF, clique em cima da Dívida de sua escolha e gere o Boleto. Em caso de dúvidas, acesse o <a href="/#atendimento">Balcão Virtual</a>, na página inicial. </p>
          </>
        ),
      },
      {
        slug: "divida-ativa-5",
        question: <><strong>Falecendo o contribuinte,</strong> a dívida fica cancelada?</>,
        answer: (
          <>
            <p> Não. O espólio responde pelos tributos devidos até a data da abertura da sucessão. O sucessor e o cônjuge meeiro respondem pelos tributos até a data da partilha ou adjudicação. </p> <p> Legislação: art. 44, incs. II e III do Código Tributário Municipal. </p>
          </>
        ),
      },
    ],
  },
  {
    slug: "certidao-negativa-e-ou-positiva",
    name: "Certidão Negativa e/ou Positiva",
    questions: [
      {
        slug: "certidao-negativa-e-ou-positiva-1",
        question: <>O que é a <strong>Certidão Negativa de Débito</strong> (CND)?</>,
        answer: (
          <>
            <p> Certidão Negativa de Débito (CND) é um documento em que a Administração Pública informa a situação de regularidade fiscal do contribuinte. Quando requerida sua emissão, a Administração Tributária tem o prazo de 10 (dez) dias para expedi-la, a contar da data do protocolo do pedido. O único órgão que expede CND – ou a Certidão Positiva com Efeitos de Negativa (CPEN) – é a Secretaria de Finanças. O pagamento e a extinção de débitos por outros motivos (prescrição, cancelamento, remissão e etc) são comunicados de forma online pela PGM à SEFIN. </p> <p> Legislação: art. 209 do Código Tributário Municipal e art. 29 do decreto nº 13.601/2015. </p>
          </>
        ),
      },
      {
        slug: "certidao-negativa-e-ou-positiva-2",
        question: <>O que é a <strong>Certidão Positiva de Débitos com Efeitos de Negativa?</strong></>,
        answer: (
          <>
            <p> É um documento no qual a Administração Pública informa que, apesar do contribuinte possuir débitos pendentes com a Fazenda Pública Municipal, estes estão com sua exigibilidade suspensa ou não são exigíveis, não podendo, portanto, ser objeto de cobrança judicial nem de protesto em cartório. </p> <p> O exemplo mais comum de suspensão da exigibilidade é quando o contribuinte parcela débitos vencidos. Outro exemplo é quando há um mandado judicial que concede medida liminar em favor do contribuinte. Já os créditos não exigíveis são, por exemplo, aqueles valores parcelados que ainda irão vencer. Vale salientar que, essa certidão terá os mesmos efeitos de uma Certidão Negativa de Débitos (CND). </p> <p> Legislação: arts. 70 e art. 210 do Código Tributário Municipal. </p>
          </>
        ),
      },
    ],
  },
  {
    slug: "credito-tributario",
    name: "Crédito tributário",
    questions: [
      {
        slug: "credito-tributario-1",
        question: <><strong>O crédito tributário</strong> pode ter a sua cobrança suspensa?</>,
        answer: (
          <>
            <p> Sim. Desde que ocorra pelo menos uma das seguintes hipóteses: moratória, parcelamento, depósito do montante integral, recurso em processo administrativo, concessão de medida liminar em mandado de segurança, ou concessão de medida liminar ou de tutela antecipada, em outras espécies de ação judicial. </p> <p> Legislação: art. 70, incs. I ao VI, do Código Tributário Municipal. </p>
          </>
        ),
      },
      {
        slug: "credito-tributario-2",
        question: <>Os <strong>débitos perante a Fazenda Municipal</strong> podem ser parcelados?</>,
        answer: (
          <>
            <p> Sim. Créditos de competência da Dívida Ativa podem ser pagos em até 30 (trinta) parcelas, mensais e sucessivas, desde que as parcelas não sejam inferiores a R$ 75,00 (setenta e cinco reais), para os parcelamentos concedidos a pessoa física, e R$ 350,00 (trezentos e cinquenta reais), para os parcelamentos concedidos a pessoa jurídica. </p> <p> Legislação: art. 42, Parágrafo Único, incs. I,II e do art. 49 caput, incs. I e II, do decreto nº 13.601/2015. </p>
          </>
        ),
      },
    ],
  },
  {
    slug: "credito-protestado",
    name: "Crédito protestado",
    questions: [
      {
        slug: "credito-protestado-1",
        question: <>O que é o <strong>Protesto?</strong></>,
        answer: (
          <>
            <p> Protesto é um dos instrumentos que a Fazenda Pública tem para cobrar um crédito inscrito em Dívida Ativa. O devedor protestado fica com crédito negativo junto aos Bancos e demais instituições financeiras, o que lhe impossibilita de contrair empréstimos. Por isso, é tão importante manter em dia as obrigações perante a Fazenda Pública. </p>
          </>
        ),
      },
      {
        slug: "credito-protestado-2",
        question: <>Onde <strong>pagar um crédito encaminhado</strong> para protesto ou protestado?</>,
        answer: (
          <>
            <p> Quando o crédito é enviado para protesto, a PGM não pode mais emitir Boleto dentro do mês do envio. Essa atividade fica a cargo do Cartório que, recebendo o valor do débito e de suas custas no prazo estabelecido em boleto, não protestará o devedor. </p> <p> Ultrapassado mês do envio e não pago o crédito perante o Cartório no prazo que este assinalar, ocorre o protesto e o devedor fica com as restrições perante o mercado. Neste caso, o contribuinte deve procurar um dos canais de atendimento da PGM para pagar ou parcelar o débito, devendo ainda recolher as custas do Cartório. Sem esse recolhimento, o devedor continua negativado. </p> <p> Legislação: art. 20, caput, § 2º e art. 22 do decreto nº 13.601/2015. </p>
          </>
        ),
      },
    ],
  },
  {
    slug: "parcelamentos",
    name: "Parcelamentos",
    questions: [
      {
        slug: "parcelamentos-1",
        question: <><strong>Como solicitar</strong> o parcelamento?</>,
        answer: (
          <>
            <p> O contribuinte deverá requerer à Procuradoria Geral do Município o parcelamento de seu débito, o que implica a confissão formal de sua dívida. Ao assinar esse requerimento, o sujeito passivo indicará o débito e o número de parcelas que deseja pagar. No entanto, esse pedido ficará sujeito, por exemplo: à entrega da documentação solicitada pela PGM, bem como à assinatura no Termo de Reconhecimento, Confissão e Acordo para Pagamento Parcelado de Dívida para com o município de Fortaleza, o qual será submetido ao crivo judicial para homologação. </p> <p> Legislação: art. 52, caput, §1º ao §3º do decreto nº 13.601/2015. </p>
          </>
        ),
      },
      {
        slug: "parcelamentos-2",
        question: <>Em que situação o <strong>parcelamento é perdido?</strong></>,
        answer: (
          <>
            <p> Quando o contribuinte deixa de pagar 03 (três) parcelas, consecutivas ou não, ou se não efetuar o pagamento da primeira parcela. Isso acarreta a perda imediata de qualquer desconto ou benefício concedido. Nesse caso, o devedor será notificado para pagar integralmente o débito, no prazo de até 30 (trinta) dias, contados da notificação. É importante lembrar que as parcelas pagas serão consideradas mera amortização da dívida anterior ao ajuste. </p> <p> Legislação: art. 38, caput, §1º, §3º e art. 56 caput, §2º do decreto nº 13.601/2015. </p>
          </>
        ),
      },
      {
        slug: "parcelamentos-3",
        question: <>É possível fazer um <strong>reparcelamento?</strong></>,
        answer: (
          <>
            <p> Sim. No entanto, existe uma penalidade, além da cobrança de correção monetária e juros sobre a dívida: o contribuinte terá a quantidade de parcelas reduzidas, em relação ao parcelamento original que firmou. Exemplo: se o primeiro parcelamento foi firmado em 30 vezes, agora só poderá ser reparcelado em 24 vezes, obedecidos os critérios de valor mínimo da parcela. </p> <p> Legislação: art. 56, §1º do decreto nº 13.601/2015. </p>
          </>
        ),
      },
    ],
  },
];
